import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import _get from 'lodash/get'
import Modal from 'react-modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCalendarAlt,
  faCheckCircle,
  faCircle
} from '@fortawesome/free-solid-svg-icons'
import { submitChiliPiperWithTracking } from '../utils/chiliPiperAffiliateTracking'
import modalStyle from '../../_common/core/modalStyle'
import Button from './Button'

// Define keyframes for the glow animation
const pulseAnimation = `
  @keyframes pulse-glow {
    0% {
      box-shadow: 0 0 15px 1px rgba(138, 43, 226, 0.4);
    }
    50% {
      box-shadow: 0 0 20px 2px rgba(138, 43, 226, 0.6), 0 0 15px 3px rgba(138, 43, 226, 0.2);
    }
    100% {
      box-shadow: 0 0 15px 1px rgba(138, 43, 226, 0.4);
    }
  }
`

const HELP_TYPE_OPTIONS = [
  'Setting up an event',
  'Explaining specific features',
  'Setting up my payout structure',
  'Understanding ticket scanning',
  'Something else'
]

const SMART_PARAMETER_ID = 'b77615a6-68ab-4b56-943f-e571429403c6'

const BookTrainingButton = ({ user }) => {
  const [isChiliPiperLoaded, setIsChiliPiperLoaded] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [selectedHelpTypes, setSelectedHelpTypes] = useState([])

  useEffect(() => {
    // Inject the animation keyframes into the document
    if (!document.getElementById('pulse-glow-animation')) {
      const styleEl = document.createElement('style')
      styleEl.id = 'pulse-glow-animation'
      styleEl.innerHTML = pulseAnimation
      document.head.appendChild(styleEl)
    }

    // Check if ChiliPiper is loaded
    if (window.ChiliPiper) {
      setIsChiliPiperLoaded(true)
    }

    // Set up responsive behavior
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    // Initial check
    checkScreenSize()

    // Add event listener for window resize
    window.addEventListener('resize', checkScreenSize)

    const handleChiliPiperLoaded = () => {
      setIsChiliPiperLoaded(true)
    }

    window.addEventListener('ChiliPiperLoaded', handleChiliPiperLoaded)

    if (!document.querySelector('script[src*="chilipiper.in/concierge-js"]')) {
      const script = document.createElement('script')
      script.src = 'https://theticketfairy.chilipiper.com/concierge-js/cjs/concierge.js'
      script.type = 'text/javascript'
      script.async = true
      script.onload = () => {
        window.dispatchEvent(new Event('ChiliPiperLoaded'))
      }
      document.head.appendChild(script)
    }

    return () => {
      window.removeEventListener('ChiliPiperLoaded', handleChiliPiperLoaded)
      window.removeEventListener('resize', checkScreenSize)
    }
  }, [])

  const handleButtonClick = () => {
    setShowModal(true)
  }

  const handleCheckboxChange = option => {
    setSelectedHelpTypes(prev => {
      if (prev.includes(option)) {
        return prev.filter(item => item !== option)
      }
      return [...prev, option]
    })
  }

  const handleSubmit = () => {
    if (selectedHelpTypes.length === 0) {
      return
    }

    setShowModal(false)

    if (window.ChiliPiper) {
      try {
        submitChiliPiperWithTracking('theticketfairy', 'onboarding-router', {
          map: true,
          trigger: 'InAppButton',
          lead: {
            firstname: _get(user, 'firstName', ''),
            lastname: _get(user, 'lastName', ''),
            email: _get(user, 'email', ''),
            [SMART_PARAMETER_ID]: selectedHelpTypes.join(';'),
          }
        })
        // Reset selections after submit
        setSelectedHelpTypes([])
      } catch (error) {
        console.error('Error calling ChiliPiper:', error)
      }
    } else {
      console.warn('ChiliPiper is not loaded yet')
      alert('Booking system is currently unavailable. Please try again later.')
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setSelectedHelpTypes([])
  }

  return (
    <div style={{
      position: 'relative',
      display: 'inline-block',
      borderRadius: '4px',
      margin: 0
    }}>
      <button
        className="btn btn-primary booking-button"
        onClick={handleButtonClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px 16px',
          borderRadius: '4px',
          fontSize: '14px',
          fontWeight: '600',
          cursor: 'pointer',
          backgroundColor: isHovered ? '#7823c7' : '#8a2be2',
          color: 'white',
          border: 'none',
          boxShadow: isHovered
            ? '0 0 12px 3px rgba(138, 43, 226, 0.4), 0 0 18px 4px rgba(138, 43, 226, 0.2)'
            : '0 0 5px 1px rgba(138, 43, 226, 0.2)',
          transition: 'all 0.3s ease',
          letterSpacing: '0.5px',
          margin: 0,
          animation: isHovered ? 'none' : 'pulse-glow 3s infinite ease-in-out',
          transform: isHovered ? 'scale(1.02)' : 'scale(1)',
          position: 'relative',
          zIndex: 1
        }}
        onMouseOver={() => setIsHovered(true)}
        onMouseOut={() => setIsHovered(false)}
      >
        <FontAwesomeIcon
          icon={faCalendarAlt}
          style={{
            marginRight: '8px',
            fontSize: '14px'
          }}
        />
        {isMobile ? 'Need Help? Book Training Call' : 'Need Help? Book On-Boarding / Training Call'}
      </button>

      <Modal
        className="modal-dialog modal-trans"
        style={modalStyle}
        isOpen={showModal}
        contentLabel="Book Training Call"
        onRequestClose={handleCloseModal}
        closeTimeoutMS={150}
        ariaHideApp={false}
      >
        <div className="modal-dialog" style={{ maxWidth: isMobile ? '95%' : '500px', margin: '0 auto' }}>
          <div className="modal-content" style={{ borderRadius: '12px', overflow: 'hidden', backgroundColor: 'rgba(32, 32, 40, 0.98)' }}>
            <div
              className="modal-header"
              style={{
                background: 'linear-gradient(135deg, #8a2be2 0%, #6b1cb0 100%)',
                padding: isMobile ? '20px 16px' : '24px',
                borderBottom: 'none',
                textAlign: 'center'
              }}
            >
              <div style={{ marginBottom: '8px' }}>
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  style={{ fontSize: '28px', color: 'rgba(255,255,255,0.9)' }}
                />
              </div>
              <p
                className="h4 text-compact"
                style={{
                  margin: 0,
                  color: 'white',
                  fontWeight: '600',
                  fontSize: isMobile ? '18px' : '20px'
                }}
              >
                Let's get you the help you need!
              </p>
              <p style={{ margin: '8px 0 0', color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
                Select the topics you'd like to discuss
              </p>
            </div>
            <div className="modal-body" style={{ padding: isMobile ? '16px' : '24px' }}>
              <div className="help-type-options">
                {HELP_TYPE_OPTIONS.map(option => {
                  const isSelected = selectedHelpTypes.includes(option)
                  return (
                    <label
                      key={option}
                      className="checkbox-item"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: isMobile ? '14px 12px' : '16px',
                        marginBottom: '10px',
                        borderRadius: '8px',
                        border: isSelected
                          ? '2px solid #8a2be2'
                          : '1px solid rgba(255, 255, 255, 0.15)',
                        backgroundColor: isSelected
                          ? 'rgba(138, 43, 226, 0.12)'
                          : 'rgba(255, 255, 255, 0.03)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        transform: isSelected ? 'scale(1.01)' : 'scale(1)'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleCheckboxChange(option)}
                        style={{ display: 'none' }}
                      />
                      <FontAwesomeIcon
                        icon={isSelected ? faCheckCircle : faCircle}
                        style={{
                          marginRight: '12px',
                          fontSize: '20px',
                          color: isSelected ? '#8a2be2' : 'rgba(255, 255, 255, 0.3)',
                          transition: 'all 0.2s ease'
                        }}
                      />
                      <span style={{
                        fontSize: isMobile ? '14px' : '15px',
                        fontWeight: isSelected ? '500' : '400',
                        color: isSelected ? '#fff' : 'rgba(255, 255, 255, 0.85)'
                      }}>
                        {option}
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>
            <div
              style={{
                padding: isMobile ? '16px' : '20px 24px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'flex-end',
                alignItems: isMobile ? 'center' : 'center',
                gap: '12px'
              }}
            >
              <Button
                className="btn btn-primary"
                type="button"
                onClick={handleSubmit}
                disabled={selectedHelpTypes.length === 0}
                style={{
                  width: isMobile ? '100%' : 'auto',
                  padding: '10px 24px',
                  backgroundColor: selectedHelpTypes.length === 0 ? '#666' : '#8a2be2',
                  border: 'none',
                  fontWeight: '600',
                  margin: 0
                }}
              >
                Continue to Book
              </Button>
              <Button
                className="btn btn-default"
                type="button"
                onClick={handleCloseModal}
                style={{
                  width: isMobile ? '100%' : 'auto',
                  padding: '10px 20px',
                  margin: 0
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export const UnconnectedBookTrainingButton = BookTrainingButton

export default connect(
  state => ({
    user: _get(state, 'auth.user', {}),
  })
)(BookTrainingButton)
